import React, { useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MESSAGE_POSITIONS } from '../../../utils/messageHelpers';
import ReactionBar from './ReactionBar';

const BubbleContainer = styled(Box)(({ theme, isSelf, position, isHovered }) => {
  const getBorderRadius = () => {
    const baseRadius = '18px';
    const tightRadius = '4px';

    if (position === MESSAGE_POSITIONS.SINGLE) return baseRadius;

    if (isSelf) {
      return {
        [MESSAGE_POSITIONS.FIRST]: `${baseRadius} ${baseRadius} ${tightRadius} ${baseRadius}`,
        [MESSAGE_POSITIONS.MIDDLE]: `${baseRadius} ${tightRadius} ${tightRadius} ${baseRadius}`,
        [MESSAGE_POSITIONS.LAST]: `${baseRadius} ${tightRadius} ${baseRadius} ${baseRadius}`,
      }[position];
    } else {
      return {
        [MESSAGE_POSITIONS.FIRST]: `${baseRadius} ${baseRadius} ${baseRadius} ${tightRadius}`,
        [MESSAGE_POSITIONS.MIDDLE]: `${tightRadius} ${baseRadius} ${baseRadius} ${tightRadius}`,
        [MESSAGE_POSITIONS.LAST]: `${tightRadius} ${baseRadius} ${baseRadius} ${baseRadius}`,
      }[position];
    }
  };

  const marginBottom = position === MESSAGE_POSITIONS.LAST || position === MESSAGE_POSITIONS.SINGLE ? '12px' : '2px';

  return {
    maxWidth: { xs: '85%', sm: '75%', md: '70%' },
    padding: { xs: '8px 12px', sm: '10px 14px' },
    marginLeft: isSelf ? 'auto' : '0',
    marginBottom,
    borderRadius: getBorderRadius(),
    background: isSelf
      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`
      : "rgba(38, 38, 42, 0.97)",
    border: `1px solid ${isSelf ? alpha(theme.palette.primary.main, 0.4) : "rgba(255,255,255,0.07)"}`,
    color: '#fff',
    position: 'relative',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: 'messageAppear 0.3s ease-out',

    '@keyframes messageAppear': {
      from: {
        opacity: 0,
        transform: 'translateY(10px) scale(0.95)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
    },

    ...(isHovered && {
      transform: 'scale(1.02)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
    }),
  };
});

const MessageText = styled(Typography)({
  fontSize: { xs: '13px', sm: '14px' },
  lineHeight: 1.4,
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
});

const MessageTimestamp = styled(Typography)(({ isVisible }) => ({
  position: 'absolute',
  bottom: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '4px 8px',
  marginBottom: '4px',
  background: alpha('#000', 0.85),
  backdropFilter: 'blur(10px)',
  borderRadius: '6px',
  fontSize: '11px',
  color: alpha('#fff', 0.9),
  whiteSpace: 'nowrap',
  opacity: isVisible ? 1 : 0,
  pointerEvents: 'none',
  transition: 'opacity 0.2s ease',
  zIndex: 5,
}));

const ReactionsList = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  marginTop: '6px',
});

const ReactionPill = styled(Box)(({ theme, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: { xs: '2px', sm: '4px' },
  padding: { xs: '3px 6px', sm: '4px 8px' },
  background: isActive
    ? alpha(theme.palette.primary.main, 0.2)
    : alpha('#000', 0.3),
  border: `1px solid ${isActive ? theme.palette.primary.main : alpha('#fff', 0.1)}`,
  borderRadius: '12px',
  fontSize: { xs: '12px', sm: '14px' },
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.15),
    transform: 'scale(1.05)',
  },
}));

const ReplyPreview = styled(Box)(({ theme }) => ({
  padding: { xs: '5px 8px', sm: '6px 10px' },
  marginBottom: '6px',
  background: alpha('#000', 0.2),
  borderLeft: `2px solid ${theme.palette.primary.main}`,
  borderRadius: '6px',
  fontSize: { xs: '11px', sm: '12px' },
  opacity: 0.8,
}));

const MessageBubble = ({
  message,
  isSelf,
  position,
  onReact,
  onReply,
  currentUserId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTimestamp, setShowTimestamp] = useState(false);

  const hasReactions = message.reactions && Object.keys(message.reactions).length > 0;

  const formatTimestamp = (timestamp) => {
    let str = String(timestamp || '').trim().replace(' ', 'T');
    if (str && !str.endsWith('Z') && !str.includes('+')) str += 'Z';
    return new Date(str).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Box
      onMouseEnter={() => {
        setIsHovered(true);
        setShowTimestamp(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowTimestamp(false);
      }}
      sx={{
        display: 'flex',
        justifyContent: isSelf ? 'flex-end' : 'flex-start',
        position: 'relative',
        px: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      <BubbleContainer isSelf={isSelf} position={position} isHovered={isHovered}>
        <MessageTimestamp isVisible={showTimestamp}>
          {formatTimestamp(message.created_at)}
        </MessageTimestamp>

        <ReactionBar
          isVisible={isHovered}
          isSelf={isSelf}
          onReact={onReact}
          messageId={message.id}
        />

        {message.reply_to && (
          <ReplyPreview>
            <Typography sx={{ fontSize: '11px', opacity: 0.7, mb: 0.5 }}>
              Reply to {message.reply_to.sender_name}
            </Typography>
            <Typography sx={{ fontSize: '12px' }}>
              {message.reply_to.message}
            </Typography>
          </ReplyPreview>
        )}

        <MessageText>{message.message}</MessageText>

        {hasReactions && (
          <ReactionsList>
            {Object.entries(message.reactions).map(([emoji, users]) => (
              <ReactionPill
                key={emoji}
                isActive={users.includes(currentUserId)}
                onClick={() => onReact(message.id, emoji)}
              >
                <span style={{ fontSize: '14px' }}>{emoji}</span>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#fff' }}>
                  {users.length}
                </Typography>
              </ReactionPill>
            ))}
          </ReactionsList>
        )}
      </BubbleContainer>
    </Box>
  );
};

export default MessageBubble;
